import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthProvider';
import { parseThemePreferences, saveThemePreferences, subscribeThemePreferences } from '@/lib/themePreferences';
import {
  applyTheme,
  loadSettings,
  nextThemeCheck,
  resolveTheme,
  saveSettings,
  systemPrefersDark,
  type Coords,
  type Theme,
  type ThemeSettings,
  DEFAULT_THEME_SETTINGS,
} from '@/lib/theme';

/**
 * A téma életciklusa egy helyen.
 *
 * - egyetlen időzítő fut, a KÖVETKEZŐ váltás időpontjára állítva
 *   (nem pollozunk percenként),
 * - a rendszerbeállítás változását figyeljük,
 * - visszatéréskor (láthatóvá váló fül) újraszámolunk, mert a telefon
 *   alvás közben nem futtatja az időzítőket,
 * - AKTÍV RÖGZÍTÉS KÖZBEN NEM VÁLTUNK: a térképstílus cseréje félbeszakítaná
 *   a futást és újratöltené a Mapbox stílust. A váltás a mentés után történik.
 */
export function useTheme(options?: { coords?: Coords | null; recordingActive?: boolean }) {
  const { user } = useAuth();
  const coords = options?.coords ?? null;
  const recordingActive = options?.recordingActive ?? false;

  const [settings, setSettings] = useState<ThemeSettings>(loadSettings);
  const [theme, setTheme] = useState<Theme>(() =>
    resolveTheme(loadSettings(), new Date(), coords, systemPrefersDark()),
  );

  const timer = useRef<number | null>(null);
  const deferred = useRef<Theme | null>(null);

  useEffect(() => {
    if (!user) return;
    const key = `grundo.theme.user.${user.uid}`;
    let cached: ThemeSettings | null = null;
    try {
      cached = parseThemePreferences(JSON.parse(localStorage.getItem(key) ?? 'null'));
      // Migrate the existing app preference once; an admin default must not
      // overwrite the preference on the play subdomain on first sign-in.
      if (!cached && !localStorage.getItem('grundo.theme.owner') && !location.hostname.startsWith('admin.')) cached = loadSettings();
    } catch { /* Storage is optional. */ }
    setSettings(cached ?? DEFAULT_THEME_SETTINGS);
    return subscribeThemePreferences(user.uid, (remote) => {
      const next = remote ?? cached ?? DEFAULT_THEME_SETTINGS;
      setSettings(next);
      saveSettings(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
        localStorage.setItem('grundo.theme.owner', user.uid);
      } catch { /* Storage is optional. */ }
      if (!remote && cached) {
        void saveThemePreferences(user.uid, cached).catch((error: unknown) => console.warn('[theme] Migration failed', error));
      }
    });
  }, [user?.uid]);

  const evaluate = useCallback(() => {
    const next = resolveTheme(settings, new Date(), coords, systemPrefersDark());

    if (recordingActive) {
      // Elhalasztjuk: a rögzítés alatti témaváltás zavaró és drága.
      deferred.current = next;
      return;
    }

    deferred.current = null;
    setTheme(next);
    applyTheme(next);
  }, [settings, coords, recordingActive]);

  // Újraszámolás + időzítő a következő váltásra
  useEffect(() => {
    evaluate();

    if (timer.current !== null) window.clearTimeout(timer.current);
    const next = nextThemeCheck(settings, new Date(), coords);
    if (next) {
      const delay = Math.max(1000, next.getTime() - Date.now());
      // setTimeout felső korlátja ~24,8 nap — a mi távjaink ezen belül vannak
      timer.current = window.setTimeout(evaluate, delay);
    }

    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [settings, coords, evaluate]);

  // A rendszerbeállítás változása (csak a 'system' mód érdekli, de olcsó)
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;
    const onChange = () => evaluate();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [evaluate]);

  // Visszatérés a háttérből: alvás alatt nem futottak az időzítők
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') evaluate();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [evaluate]);

  // A rögzítés végén alkalmazzuk az elhalasztott váltást
  useEffect(() => {
    if (!recordingActive && deferred.current) {
      const next = deferred.current;
      deferred.current = null;
      setTheme(next);
      applyTheme(next);
    }
  }, [recordingActive]);

  const update = useCallback((patch: Partial<ThemeSettings>) => {
    setSettings((current) => {
      const next = { ...current, ...patch };
      saveSettings(next);
      if (user) {
        try { localStorage.setItem(`grundo.theme.user.${user.uid}`, JSON.stringify(next)); } catch { /* Storage is optional. */ }
        void saveThemePreferences(user.uid, next).catch((error: unknown) => console.warn('[theme] Save failed', error));
      }
      return next;
    });
  }, [user?.uid]);

  return { theme, settings, update };
}
